/**
 * ContagemCompletaPage - EPIC 19 - Interface Completa de Contagem
 *
 * Refatorado para usar hooks customizados (Epic 19 Refactoring 2.0)
 * Abas: Agendamento | Dados da Contagem | Pós Contagem | Itens | Cirurgias a Faturar | Fotografias | Relatórios
 */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/MainLayout';
import contagemService from '../services/contagemService';
import useAuthStore from '../stores/authStore';
import { msg, MESSAGES } from '../utils/messages';
import { useConfirm } from '../hooks/useConfirm';
import { STATUS_MAP } from '../constants/status';
import { useCirurgiaMatching } from '../hooks/useCirurgiaMatching';
import { useConferenciaLacresMatching } from '../hooks/useConferenciaLacresMatching';

// Custom hooks (Epic 19 Refactoring)
import {
  useContagemModals,
  useContagemNavigation,
  useContagemLookups,
  useContagemUploads,
  useContagemData,
  useContagemEdit,
  useContagemAuditores,
  useContagemJobLoader,
  useContagemSave,
  useContagemExcel,
  useContagemGlobalFilter,
  useContagemLocaisPorCliente,
} from '../hooks';

// New extracted hooks
import { useContagemItemActions } from '../hooks/useContagemItemActions';
import { useContagemPageEffects } from '../hooks/useContagemPageEffects';

// Sub-components (barrel import from index.ts)
import {
  AgendamentoTab,
  DadosContagemTab,
  ItensContagemTab,
  CirurgiasTab,
  ImportExcelModal,
  ReportModal,
  ImportInformacoesModal,
  InfoModal,
  FloatingActionButtons,
  ContagemHeader,
  ContagemTabs,
  RelatoriosContagemTab,
} from '../components/contagem';
import { FotografiasTab } from '../components/contagem/FotografiasTab';
import { AddItemModal } from '../components/contagem/AddItemModal';
import ImportOrdemContagemModal from '../components/contagem/ImportOrdemContagemModal';
import ImportResultadoContagemModal from '../components/contagem/ImportResultadoContagemModal';
import IntervaloAgendamentoModal from '../components/IntervaloAgendamentoModal';

export default function ContagemCompletaPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { confirm, ConfirmDialog } = useConfirm();
  const { user } = useAuthStore();
  const isMaster = user?.role === 'MASTER';

  // ==================== HOOKS ====================

  const modals = useContagemModals();
  const navigation = useContagemNavigation(id);
  const { isNewContagem, dateParam } = navigation;

  const jobLoader = useContagemJobLoader(id, isNewContagem, dateParam);
  const {
    loading, error, setError,
    formData, setFormData,
    selectedClienteIds, setSelectedClienteIds,
    jobClientes, setJobClientes,
    selectedHospitalId, setSelectedHospitalId,
    loadJobAuditores, loadJobLocais,
  } = jobLoader;

  const globalFilterHook = useContagemGlobalFilter(jobClientes);
  const { globalClienteFilter, setGlobalClienteFilter, globalClienteOptions } = globalFilterHook;

  const editMode = useContagemEdit(isNewContagem);
  const {
    isEditMode, setIsEditMode, isReadOnly,
    modifiedFields, setModifiedFields,
    originalFormData, setOriginalFormData,
    trackFieldModification, trackArrayModification,
    clearModifiedFields, hasModifications,
    legacyLocationId, setLegacyLocationId,
    originalHospitalId, setOriginalHospitalId,
  } = editMode;

  const lookups = useContagemLookups(selectedHospitalId, selectedClienteIds);
  const {
    clientesDisponiveis, loadingClientes,
    hospitaisDisponiveis, allHospitais, loadingHospitais,
    locaisContagemDisponiveis,
    auditoresDisponiveis, loadingAuditores, errorAuditores,
    motivosOptions,
  } = lookups;

  const auditoresHook = useContagemAuditores();
  const {
    auditoresParticipantes, setAuditoresParticipantes,
    selectedAuditorId, setSelectedAuditorId,
    locaisContagemSelecionados, setLocaisContagemSelecionados,
    selectedLocalId, setSelectedLocalId,
    pontosContagem, setPontosContagem,
  } = auditoresHook;

  // Epic LOCAIS-POR-CLIENTE Story 8: hook for grouped locais per cliente
  const locaisPorClienteHook = useContagemLocaisPorCliente(
    selectedHospitalId,
    selectedClienteIds,
  );

  const dataHook = useContagemData();
  const {
    dadosContagem, setDadosContagem,
    originalDadosContagem,
    modifiedItems, selectedRow, setSelectedRow,
    updateItem, resetData, saveOriginalData,
    clearModifiedItems,
    totalSobras, totalFaltas, totalItems, itemsWithDivergence,
  } = dataHook;

  const cirurgiaMatching = useCirurgiaMatching(id ? parseInt(id) : 0);
  const conferenciaLacresMatching = useConferenciaLacresMatching(id ? parseInt(id) : 0);

  const uploads = useContagemUploads();
  const {
    uploadResult,
    selectedImportClient, selectedRegime,
    setUploadResult,
    setSelectedImportClient, setSelectedRegime,
  } = uploads;

  const saveHook = useContagemSave({
    id,
    isNewContagem,
    formData,
    selectedClienteIds,
    selectedHospitalId,
    originalHospitalId,
    // N165: o alerta de intervalo so pode aparecer quando a DATA (ou o cliente)
    // muda -- nao a cada gravacao. Os dois ja existiam nesta pagina.
    modifiedFields,
    originalFormData,
    legacyLocationId,
    auditoresParticipantes,
    locaisContagemSelecionados,
    // Epic LOCAIS-POR-CLIENTE: send grouped payload via hook
    clienteLocaisPayload: locaisPorClienteHook.getClienteLocaisPayload(),
    allLocaisFlat: locaisPorClienteHook.getAllLocaisFlat(),
    setIsEditMode,
    setError,
  });

  const {
    showIntervaloModal,
    intervaloValidacoes,
    intervaloDataSelecionada,
    intervaloLoading,
    handleIntervaloConfirm,
    handleIntervaloConfirmWithJustificativas,
    handleIntervaloCancel,
  } = saveHook;

  const [posContagemRefreshKey, setPosContagemRefreshKey] = useState(0);

  const excelHook = useContagemExcel({
    id,
    jobClientes,
    selectedImportClient,
    selectedRegime,
    setSelectedImportClient,
    setSelectedRegime,
    setUploadResult,
    closeImportModal: modals.closeImportModal,
    closeImportInformacoesModal: modals.closeImportInformacoesModal,
    setFormData,
    setLocaisContagemSelecionados,
    onPosContagemImportSuccess: () => setPosContagemRefreshKey(k => k + 1),
  });

  const {
    uploading, downloading, uploadingInformacoes,
    selectedFile, selectedInformacoesFile,
    setSelectedFile, setSelectedInformacoesFile,
  } = excelHook;

  // ==================== LOCAL STATE ====================
  const [hideLotsAndValidity, setHideLotsAndValidity] = useState(false);
  const statusOptions = Object.values(STATUS_MAP);

  // ==================== ITEM ACTIONS HOOK ====================
  const itemActions = useContagemItemActions({
    id,
    isNewContagem,
    setDadosContagem,
    setUploadResult,
    confirm,
  });

  const {
    addingItem,
    showAddItemModal, setShowAddItemModal,
    showImportOrdemModal, setShowImportOrdemModal,
    showImportResultadoModal, setShowImportResultadoModal,
    handleAddItem,
    handleDeleteItem,
    handleImportOrdemSuccess,
    handleImportResultadoSuccess,
  } = itemActions;

  // ==================== PAGE EFFECTS + FORM HANDLERS HOOK ====================
  const pageEffects = useContagemPageEffects({
    id,
    isNewContagem,
    dateParam,
    formData,
    setFormData,
    selectedClienteIds,
    selectedHospitalId,
    auditoresParticipantes,
    locaisContagemSelecionados,
    isEditMode,
    originalFormData,
    setOriginalFormData,
    dadosContagem,
    originalDadosContagem,
    setDadosContagem,
    setUploadResult,
    uploadResult,
    modifiedItems,
    modifiedFields,
    setModifiedFields,
    clearModifiedItems,
    saveOriginalData,
    setIsEditMode,
    setAuditoresParticipantes,
    setLocaisContagemSelecionados,
    setSelectedHospitalId,
    selectedAuditorId,
    setSelectedAuditorId,
    selectedLocalId,
    setSelectedLocalId,
    auditoresDisponiveis,
    locaisContagemDisponiveis,
    hospitaisDisponiveis,
    loadJobAuditores,
    loadJobLocais,
    trackFieldModification,
    trackArrayModification,
    cirurgiaMatching,
    conferenciaLacresMatching,
    saveHook,
  });

  const {
    handleChange,
    handleSelectHospital,
    handleAddAuditor,
    handleRemoveAuditor,
    handleAddLocal,
    handleRemoveLocal,
    handleSaveAll,
    handleDiscardChanges,
    formatEnvioSaldos,
  } = pageEffects;

  const handleUpdateItemField = (rowIndex: number, field: string, value: number | string) => {
    updateItem(rowIndex, field as any, value);
    setModifiedFields(prev => new Set(prev).add(`item_${rowIndex}_${field}`));
  };

  // ==================== RENDER ====================
  return (
    <>
      <ConfirmDialog />
      <MainLayout>
        <main className="p-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full mx-auto">
            <ContagemHeader
              id={id}
              formData={formData}
              isNewContagem={isNewContagem}
              isEditMode={isEditMode}
              modifiedFields={modifiedFields}
              allHospitais={allHospitais}
              selectedHospitalId={selectedHospitalId}
              navigation={navigation}
              onNavigateBack={navigation.handleVoltar}
              globalClienteOptions={globalClienteOptions}
              globalClienteFilter={globalClienteFilter}
              setGlobalClienteFilter={setGlobalClienteFilter}
              showGlobalFilter={navigation.activeTab !== 'agendamento'}
            />

            <ContagemTabs
              activeTab={navigation.activeTab}
              setActiveTab={navigation.setActiveTab}
              isNewContagem={isNewContagem}
              isReadOnly={isReadOnly}
              isEditMode={isEditMode}
              setIsEditMode={setIsEditMode}
              onDelete={() => saveHook.handleDelete(confirm)}
            />

            <div className="min-h-[400px]">
              {navigation.activeTab === 'agendamento' && (
                <AgendamentoTab
                  formData={formData}
                  isNewContagem={isNewContagem}
                  isReadOnly={isReadOnly}
                  isEditMode={isEditMode}
                  modifiedFields={modifiedFields}
                  user={user}
                  statusOptions={statusOptions}
                  clientesDisponiveis={clientesDisponiveis}
                  loadingClientes={loadingClientes}
                  selectedClienteIds={selectedClienteIds}
                  setSelectedClienteIds={setSelectedClienteIds}
                  hospitaisDisponiveis={hospitaisDisponiveis}
                  loadingHospitais={loadingHospitais}
                  selectedHospitalId={selectedHospitalId}
                  auditoresDisponiveis={auditoresDisponiveis}
                  loadingAuditores={loadingAuditores}
                  errorAuditores={errorAuditores}
                  auditoresParticipantes={auditoresParticipantes}
                  selectedAuditorId={selectedAuditorId}
                  setSelectedAuditorId={setSelectedAuditorId}
                  locaisContagemDisponiveis={locaisContagemDisponiveis}
                  loadingLocais={false}
                  locaisContagemSelecionados={locaisContagemSelecionados}
                  selectedLocalId={selectedLocalId}
                  setSelectedLocalId={setSelectedLocalId}
                  handleAddLocal={handleAddLocal}
                  handleRemoveLocal={handleRemoveLocal}
                  locaisPorClienteHook={locaisPorClienteHook}
                  motivosOptions={motivosOptions}
                  formatEnvioSaldos={formatEnvioSaldos}
                  handleChange={handleChange}
                  handleSelectHospital={handleSelectHospital}
                  handleAddAuditor={handleAddAuditor}
                  handleRemoveAuditor={handleRemoveAuditor}
                  handleSave={saveHook.handleSave}
                  handleCancel={navigation.handleVoltar}
                  originalFormData={originalFormData}
                  setModifiedFields={setModifiedFields}
                />
              )}

              {navigation.activeTab === 'dados_contagem' && (
                <DadosContagemTab
                  isNewContagem={id === 'new'}
                  openImportInformacoesModal={modals.openImportInformacoesModal}
                  refreshKey={posContagemRefreshKey}
                  globalClienteFilter={globalClienteFilter}
                  jobClientes={jobClientes}
                  jobStatus={formData.status}
                  isMaster={isMaster}
                />
              )}

              {navigation.activeTab === 'itens' && (
                <ItensContagemTab
                  dadosContagem={dadosContagem}
                  isReadOnly={isReadOnly}
                  isEditMode={isEditMode}
                  hideLotsAndValidity={hideLotsAndValidity}
                  setHideLotsAndValidity={setHideLotsAndValidity}
                  modifiedItems={modifiedItems}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                  downloading={downloading}
                  handleDownloadExcel={excelHook.handleDownloadExcel}
                  handleUpdateItemField={handleUpdateItemField}
                  openImportModal={modals.openImportModal}
                  openInfoItensModal={modals.openInfoItensModal}
                  openReportModal={modals.openReportModal}
                  closeReportModal={modals.closeReportModal}
                  showReportModal={modals.showReportModal}
                  onOpenAddModal={() => setShowAddItemModal(true)}
                  onDeleteItem={handleDeleteItem}
                  onOpenImportOrdemModal={() => setShowImportOrdemModal(true)}
                  onOpenImportResultadoModal={() => setShowImportResultadoModal(true)}
                  isMaster={isMaster}
                  globalClienteFilter={globalClienteFilter}
                  jobClientes={jobClientes}
                  serverTotals={
                    uploadResult?.total_sistema != null
                      ? {
                          totalSistema: parseFloat(uploadResult.total_sistema),
                          totalContado: parseFloat(uploadResult.total_contado ?? '0'),
                        }
                      : null
                  }
                />
              )}

              {navigation.activeTab === 'cirurgias' && id && (
                <CirurgiasTab
                  jobId={parseInt(id)}
                  jobClientes={jobClientes}
                  isReadOnly={isReadOnly}
                  isEditMode={isEditMode}
                  globalClienteFilter={globalClienteFilter}
                />
              )}

              {navigation.activeTab === 'fotografias' && (
                <div className="p-6">
                  <FotografiasTab
                    jobId={id ? parseInt(id) : undefined}
                    globalClienteFilter={globalClienteFilter}
                    jobClientes={jobClientes}
                  />
                </div>
              )}

              {navigation.activeTab === 'relatorios' && id && (
                <div className="p-6">
                  <RelatoriosContagemTab
                    jobId={parseInt(id)}
                    globalClienteFilter={globalClienteFilter}
                    jobClientes={jobClientes}
                  />
                </div>
              )}
            </div>
          </div>
        </main>
      </MainLayout>

      {/* Modals */}
      {modals.showImportModal && (
        <ImportExcelModal
          error={error}
          selectedFile={selectedFile}
          uploading={uploading}
          uploadResult={uploadResult}
          jobClientes={jobClientes}
          selectedImportClient={selectedImportClient}
          selectedRegime={selectedRegime}
          setSelectedFile={setSelectedFile}
          setSelectedImportClient={setSelectedImportClient}
          setSelectedRegime={setSelectedRegime}
          handleUploadExcel={excelHook.handleUploadExcel}
          closeImportModal={modals.closeImportModal}
          setError={setError}
        />
      )}

      {modals.showReportModal && (
        <ReportModal
          closeReportModal={modals.closeReportModal}
          handleReportGeneration={(type) => {
            msg.info(MESSAGES.COUNTING.REPORT_NOT_IMPLEMENTED(type));
            modals.closeReportModal();
          }}
        />
      )}

      {modals.showImportInformacoesModal && (
        <ImportInformacoesModal
          selectedInformacoesFile={selectedInformacoesFile}
          uploadingInformacoes={uploadingInformacoes}
          setSelectedInformacoesFile={setSelectedInformacoesFile}
          handleImportInformacoesExcel={excelHook.handleImportInformacoesExcel}
          closeImportInformacoesModal={modals.closeImportInformacoesModal}
        />
      )}

      {modals.showInfoInformacoesModal && (
        <InfoModal type="informacoes" onClose={modals.closeInfoInformacoesModal} />
      )}

      {modals.showInfoItensModal && (
        <InfoModal type="itens" onClose={modals.closeInfoItensModal} />
      )}

      <ImportOrdemContagemModal
        isOpen={showImportOrdemModal}
        onClose={() => setShowImportOrdemModal(false)}
        jobId={id ? parseInt(id) : 0}
        onImportSuccess={handleImportOrdemSuccess}
      />

      <ImportResultadoContagemModal
        isOpen={showImportResultadoModal}
        onClose={() => setShowImportResultadoModal(false)}
        jobId={id ? parseInt(id) : 0}
        onImportSuccess={handleImportResultadoSuccess}
      />

      <AddItemModal
        isOpen={showAddItemModal}
        onClose={() => setShowAddItemModal(false)}
        onSubmit={handleAddItem}
        jobClientes={jobClientes}
        loading={addingItem}
      />

      <IntervaloAgendamentoModal
        isOpen={showIntervaloModal}
        onClose={handleIntervaloCancel}
        onConfirm={handleIntervaloConfirm}
        onConfirmWithJustificativas={handleIntervaloConfirmWithJustificativas}
        validacoes={intervaloValidacoes}
        dataSelecionada={intervaloDataSelecionada}
        loading={intervaloLoading}
      />

      {isEditMode && (
        <FloatingActionButtons
          onSaveAll={handleSaveAll}
          onDiscardChanges={handleDiscardChanges}
        />
      )}
    </>
  );
}
